import {Button, Flag} from 'platform/components';
import {
  Box,
  HStack,
  Integer,
  Show,
  ThemeColorTextPath,
  useWindowDimensions,
} from 'platform/foundation';

import {useRef, useState} from 'react';

import {suffixTestId, TestIdProps, useToggle} from 'shared/web';

import {useCatalogue} from '../../../hooks/useCatalogue';
import i18n from '../../../i18n/i18n';
import {getFeaturesVisible} from './utils/getFeaturesVisible';

const SHOWN_ROWS = 1;
const EXPAND_BUTTON_WIDTH = 44;

interface FeatureListRowProps extends TestIdProps {
  spacing: Integer;
  textColor: ThemeColorTextPath;
  values?: string[] | null;
}

export function FeatureListRow(props: FeatureListRowProps) {
  const {getFeatureLabel} = useCatalogue();
  const [isExpanded, toggleExpanded] = useToggle(false);
  const [featuresVisibleCount, setFeaturesVisibleCount] = useState(props.values?.length ?? 0);
  const lastVisibleElementRef = useRef<HTMLDivElement>(null);
  const lastVisibleElementIndexRef = useRef<number>(null);
  const {width: windowWidth} = useWindowDimensions();

  const calculateVisibleElement = (ref: HTMLDivElement | null, index: number) => {
    if (ref) {
      requestAnimationFrame(() => {
        const rect = ref.getBoundingClientRect();
        const parentRect = ref.parentElement?.getBoundingClientRect();

        const row = Math.floor((rect.top - (parentRect?.top ?? 0)) / rect.height) + 1;
        const isRowLastVisible = row === SHOWN_ROWS;
        const isRowFirstNonVisible = row === SHOWN_ROWS + 1;
        const isElementLastAndStillVisible =
          index === (props.values?.length ?? 0) - 1 && row <= SHOWN_ROWS;

        if (isRowLastVisible) {
          lastVisibleElementRef.current = ref;
          lastVisibleElementIndexRef.current = index;
        }

        if (isRowFirstNonVisible) {
          const lastIndex = lastVisibleElementIndexRef.current ?? 0;
          const lastRect = lastVisibleElementRef.current?.getBoundingClientRect();

          setFeaturesVisibleCount(
            getFeaturesVisible(EXPAND_BUTTON_WIDTH + props.spacing, lastIndex, lastRect, parentRect)
          );
        }

        if (isElementLastAndStillVisible) {
          setFeaturesVisibleCount(
            isRowLastVisible
              ? getFeaturesVisible(EXPAND_BUTTON_WIDTH + props.spacing, index, rect, parentRect)
              : index + 1
          );
        }
      });
    }
  };

  const visibleFeatures = isExpanded ? props.values : props.values?.slice(0, featuresVisibleCount);
  const hiddenItemsCount = (props.values?.length ?? 0) - featuresVisibleCount;
  const hasMoreItems = hiddenItemsCount > 0;

  return (
    <Box>
      {/* Hidden container for calculating item positioning */}
      <Box height={0} overflow="hidden" key={`calculator-${windowWidth}`}>
        <HStack spacing={props.spacing} wrap>
          {props.values?.map((feature, index) => (
            // Unfortunatelly, we do not have an unique key
            // eslint-disable-next-line react/no-array-index-key
            <div key={`${feature}-${index}`} ref={(ref) => calculateVisibleElement(ref, index)}>
              <Flag label={getFeatureLabel(feature)} isSubtle />
            </div>
          ))}
        </HStack>
      </Box>

      {/* Visible highlights */}
      <HStack spacing={props.spacing} wrap>
        {visibleFeatures?.map((feature, index) => (
          <Flag
            // Unfortunatelly, we do not have an unique key
            // eslint-disable-next-line react/no-array-index-key
            key={`${feature}-${index}`}
            label={getFeatureLabel(feature)}
            isSubtle
            colorScheme="blue"
            data-testid={suffixTestId(`value[${index}]`, props)}
          />
        ))}

        <Show when={!isExpanded && hasMoreItems}>
          <Button
            title={`${hiddenItemsCount} +`}
            size="small"
            onClick={toggleExpanded}
            data-testid={suffixTestId('expandButton', props)}
          />
        </Show>

        <Show when={isExpanded && hasMoreItems}>
          <Button
            title={i18n.t('entity.list.hide')}
            size="small"
            onClick={toggleExpanded}
            data-testid={suffixTestId('collapseButton', props)}
          />
        </Show>
      </HStack>
    </Box>
  );
}
